import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { emailValidationRegex } from '../../../../constants/regex';
import { SettingsUserDetailsFormType } from '../../../../types/types';
import { useUser } from '../../../../contexts/UserContext';
import { useNetwork } from '../../../../utils/network';
import { useShowSnackbar } from '../../../../utils/snackbar';
import { validateNameValue } from '../../../../utils/nameValidation';
import { publicIp } from 'public-ip';
import { Country } from 'country-state-city';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';

type CountryOption = {
  isoCode: string;
  name: string;
  phonecode: string;
};

const countries = Country.getAllCountries()
  .map((country) => ({
    isoCode: country.isoCode,
    name: country.name,
    phonecode: country.phonecode.replace(/[^\d]/g, '')
  }))
  .filter((country) => country.phonecode);

const findCountryOption = (countryValue?: string): CountryOption => {
  const normalized = String(countryValue || '')
    .trim()
    .toLowerCase();

  if (normalized) {
    const match = countries.find(
      (country) =>
        country.isoCode.toLowerCase() === normalized ||
        country.name.toLowerCase() === normalized
    );

    if (match) return match;
  }

  return countries.find((country) => country.isoCode === 'AU') || countries[0];
};

const getLocalPhone = (phone: string | undefined, countryCode: string) => {
  const digits = String(phone || '').replace(/\D/g, '');

  if (!digits) return '';
  if (countryCode && digits.startsWith(countryCode)) {
    return digits.slice(countryCode.length);
  }

  return digits;
};

const validatePhone = (
  localPhone: string,
  countryIso2: string,
  callingCode: string
) => {
  const local = localPhone.replace(/\s/g, '').trim();

  if (!local) return 'Phone number is required.';
  if (!/^\d+$/.test(local)) return 'Phone number can only contain numbers.';

  try {
    const parsed = parsePhoneNumberFromString(
      `+${callingCode}${local}`,
      countryIso2 as CountryCode
    );

    if (!parsed || !parsed.isPossible() || !parsed.isValid()) {
      return 'Enter a valid phone number for your country.';
    }
  } catch {
    return 'Enter a valid phone number.';
  }

  return '';
};

export const SettingsUserDetails = () => {
  const { user, setUser } = useUser();
  const { get, put } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const initialCountry = useMemo(() => findCountryOption(user.country), [user]);
  const [phoneError, setPhoneError] = useState('');
  const defaultValues: SettingsUserDetailsFormType = {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    username: user.username ?? '',
    phone: getLocalPhone(user.phone, initialCountry.phonecode),
    discordName: user.discordName
  };
  const [savedValues, setSavedValues] =
    useState<SettingsUserDetailsFormType>(defaultValues);
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset
  } = useForm({ defaultValues });

  useEffect(() => {
    let cancelled = false;

    const loadLatestUserDetails = async () => {
      try {
        const result = await get('/user/details', true);
        const latestUser = result.user;

        if (cancelled || !latestUser) return;

        const latestCountry = findCountryOption(latestUser.country);

        setUser({
          ...user,
          ...latestUser
        });

        const latestValues = {
          firstName: latestUser.firstName ?? user.firstName,
          lastName: latestUser.lastName ?? user.lastName,
          email: latestUser.email ?? user.email,
          username: latestUser.username ?? '',
          phone: getLocalPhone(latestUser.phone, latestCountry.phonecode),
          discordName: latestUser.discordName ?? ''
        };

        reset(latestValues);
        setSavedValues(latestValues);
      } catch (error) {
        console.error('Failed to load latest user details:', error);
      }
    };

    loadLatestUserDetails();

    return () => {
      cancelled = true;
    };
  }, []);

  const watchedValues = watch();
  const phoneChanged = watchedValues.phone !== savedValues.phone;
  const isFormUnchanged =
    JSON.stringify(watchedValues) === JSON.stringify(savedValues);

  const onSubmit: SubmitHandler<SettingsUserDetailsFormType> = async (
    data: any
  ) => {
    try {
      if (phoneChanged) {
        const phoneValidationMessage = validatePhone(
          data.phone,
          initialCountry.isoCode,
          initialCountry.phonecode
        );

        if (phoneValidationMessage) {
          setPhoneError(phoneValidationMessage);
          showSnackbar(phoneValidationMessage, 'error');
          return;
        }
      }

      let phoneResult: any = null;

      if (phoneChanged) {
        phoneResult = await put(
          '/user/phone',
          {
            phone: data.phone,
            countryCode: initialCountry.phonecode
          },
          true
        );
      }

      const result = await put(
        '/user/details',
        {
          firstName: data.firstName,
          lastName: data.lastName,
          username: data.username,
          //email: data.email
          discordName: data.discordName,
          ipAddress: await publicIp()
        },
        true
      );

      setUser({
        ...user,
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        //email: data.email,
        phone: phoneResult?.phone ?? user.phone,
        phoneVerified: phoneResult?.phoneVerified ?? user.phoneVerified,
        country: phoneResult?.country ?? user.country,
        state: phoneResult?.state ?? user.state,
        discordName: data.discordName,
        tokenAmount: result.tokenAmount
      });

      const nextSavedValues = {
        ...data,
        phone: getLocalPhone(
          phoneResult?.phone ?? `+${initialCountry.phonecode}${data.phone}`,
          initialCountry.phonecode
        )
      };

      reset(nextSavedValues);
      setSavedValues(nextSavedValues);
      showSnackbar(phoneResult?.message || result.message, 'success');
    } catch (error: any) {
      const message =
        error?.message ||
        error?.response?.data?.message ||
        'Could not update user details.';
      setPhoneError(message);
      showSnackbar(message, 'error');
    }
  };

  return (
    <Box
      sx={{
        margin: '0 1rem 0 1rem'
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4}>
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: 'Please enter your first name',
              // Match the server, which validates only on change: grandfather the
              // stored name so a legacy value (with characters from before this
              // rule) doesn't block other profile edits.
              validate: (value: string) =>
                value === (user.firstName ?? '')
                  ? true
                  : validateNameValue(value)
            }}
            render={({ field }) => (
              <TextField
                id="firstName"
                margin="normal"
                required
                fullWidth
                label="First name"
                error={Boolean(errors.firstName)}
                helperText={errors.firstName?.message as string}
                {...field}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{
              required: 'Please enter your last name',
              // Grandfather the stored name (validate only on change), same as
              // firstName above and the username rule below.
              validate: (value: string) =>
                value === (user.lastName ?? '')
                  ? true
                  : validateNameValue(value)
            }}
            render={({ field }) => (
              <TextField
                id="lastName"
                margin="normal"
                required
                fullWidth
                label="Last name"
                error={Boolean(errors.lastName)}
                helperText={errors.lastName?.message as string}
                {...field}
              />
            )}
          />
          <Controller
            name="username"
            control={control}
            rules={{
              required: 'Please enter your username',
              // Match the server, which validates only on change: grandfather the
              // stored username so legacy names (spaces / symbols / over-20 from
              // before this rule) don't block other profile edits. New input is
              // already stripped to valid characters by onChange below.
              validate: (value: string) => {
                if (value === (user.username ?? '')) return true;
                if (value.length > 20)
                  return 'Username cannot exceed 20 characters';
                if (!/^[A-Za-z0-9_]+$/.test(value))
                  return 'Only letters, numbers and underscores are allowed';
                return true;
              }
            }}
            render={({ field }) => (
              <TextField
                id="username"
                margin="normal"
                required
                fullWidth
                label="Username"
                inputProps={{ maxLength: 20 }}
                error={Boolean(errors.username)}
                helperText={
                  (errors.username?.message as string) ||
                  'Letters, numbers and underscores only'
                }
                {...field}
                // Enforce the charset as you type — strip anything that isn't a
                // letter, number or underscore (mirrors the server rule; also covers paste).
                onChange={(e) =>
                  field.onChange(e.target.value.replace(/[^A-Za-z0-9_]/g, ''))
                }
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              required: true,
              pattern: emailValidationRegex
            }}
            render={({ field }) => (
              <TextField
                id="email"
                margin="normal"
                required
                fullWidth
                label="Email"
                autoComplete="email"
                disabled
                error={Boolean(errors.email)}
                helperText={errors.email && 'Please enter a valid email'}
                {...field}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                id="phone"
                margin="normal"
                required
                fullWidth
                label="Phone number"
                onChange={(event) => {
                  field.onChange(event.target.value.replace(/[^\d\s]/g, ''));
                  setPhoneError('');
                }}
                error={Boolean(errors.phone) || Boolean(phoneError)}
                helperText={
                  phoneError ||
                  (errors.phone && 'Please enter your phone number') ||
                  (user.phoneVerified === false
                    ? 'This phone number has not been verified yet.'
                    : ' ')
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontWeight: 800 }}>
                        +{initialCountry.phonecode}
                      </Typography>
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  inputMode: 'tel',
                  autoComplete: 'tel-national'
                }}
              />
            )}
          />

          <Controller
            name="discordName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="discordName"
                margin="normal"
                fullWidth
                label="Discord Name"
                helperText="Please enter your full Discord username without numbers or # (e.g., username)"
                // Strip < > as you type so a Discord name can never carry an HTML
                // tag (mirrors the server sanitiser + the username field above).
                onChange={(e) =>
                  field.onChange(e.target.value.replace(/[<>]/g, ''))
                }
              />
            )}
          />

          <Button
            variant="contained"
            type="submit"
            disabled={isFormUnchanged}
            sx={{
              fontWeight: 800,
              color: '#04222a',
              backgroundColor: '#38e8ff',
              borderRadius: '10px',
              py: 1.1,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#5fefff', boxShadow: 'none' }
            }}
          >
            Save changes
          </Button>
        </Stack>
      </form>
    </Box>
  );
};
